import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  Button,
  CheckCircleOutilineIcon,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  showToast,
  TableEditable,
  TableEditableColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'

import { EmptyState } from '@/components'
import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import {
  formatCurrency,
  formatDate,
  formatNumber,
  trimString,
  useParamsState,
} from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { QUERIES_ADMIN, useForecastList, useUpdateForecast } from '@/services'
import { SupplierForecastAgreementEnum, SupplierForecastProps } from '@/types'

import { DrawerSupplierForecastFilter } from '../../../../components'
import styles from './supplier-forecast-editable-table.module.css'

export const SupplierForecastEditableTable = () => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSuppliers')

  const queryClient = useQueryClient()

  const { mutateAsync: mutateAsyncUpdateForecast } = useUpdateForecast()

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `20`,
    search: '',
  })

  const [queryParams, setQueryParams] = useParamsState({
    supplierProductFilterForm: '',
  })

  const { data, isLoading } = useForecastList({
    params: {
      ...filter,
      limit: filter?.limit || 20,
    },
  })

  const [totalPages, setTotalPages] = useState(1)

  const handleFilter = (changedKey: string, newValue: string) => {
    setFilter({ ...filter, [changedKey]: newValue })
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'table' &&
        key !== 'offset' &&
        key !== 'supplierUuid' &&
        key !== 'supplierProductFilterForm' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const onForecastSubmit = async (
    record: SupplierForecastProps,
    key: string,
    value: string | number | boolean | undefined,
  ) => {
    const response = await mutateAsyncUpdateForecast({
      uuid: record.uuid,
      [key]: value,
    })

    if (response.success) {
      showToast({
        variant: 'success',
        title: t('supplierForecastsTableUpdatedColumnSuccessTitle'),
        icon: <CheckCircleOutilineIcon />,
      })

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_FORECAST.module],
      })
    }
  }

  const columns: TableEditableColumnProps<SupplierForecastProps>[] = [
    {
      title: t('supplierForecastsTableName'),
      key: 'name',
      dataKey: 'catalogProduct',
      isEditable: false,
      render: ({ record }) => (
        <Tooltip
          text={
            record?.supplierProduct?.catalogProduct?.attributes?.title || ''
          }
          orientation="top"
          className={styles['product-column']}
        >
          <Link
            to={`/${ROUTES_ADMIN.CATALOG.PRODUCT.path}/${record?.supplierProduct?.catalogProduct?.uuid}`}
          >
            {record?.supplierProduct?.catalogProduct?.attributes?.title}
          </Link>
        </Tooltip>
      ),
    },
    {
      title: t('supplierForecastsTableSupplierLabel'),
      key: 'supplierUuid',
      width: '9.6rem',
      isEditable: false,
      render: ({ record }) => (
        <Tooltip
          text={record?.supplierProduct?.supplier?.name || ''}
          orientation="top"
          className={styles['supplier-column']}
        >
          <div>
            {trimString(
              record?.supplierProduct?.supplier?.name || '',
              14,
              '...',
            )}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('supplierForecastsTableLocation'),
      key: 'supplierWarehouseUuid',
      dataKey: 'supplierWarehouseUuid',
      isEditable: false,
      width: '12rem',
      render: ({ record }) => {
        return (
          <div className={styles['warehouse-column']}>
            {record?.supplierProduct?.supplierWarehouse?.name ? (
              <>
                <img
                  src={
                    record?.supplierProduct?.supplierWarehouse?.country === 'US'
                      ? '/images/flag-us.svg'
                      : '/images/flag-mx.svg'
                  }
                  alt={'flag'}
                />
                {record?.supplierProduct?.supplierWarehouse?.name}
              </>
            ) : (
              '-'
            )}
          </div>
        )
      },
    },
    {
      title: t('supplierForecastsTableAgreementLabel'),
      key: 'agreementType',
      sortKey: 'agreement_type',
      value: (record) => record?.agreementType,
      width: '8.5rem',
      editableProps: {
        type: 'select',
        hasTooltip: false,
        inputProps: {
          options: [
            {
              label: t('selectEmpty'),
              value: '',
            },
            ...Object.keys(SupplierForecastAgreementEnum).map((key) => ({
              label: tEnums(`forecast_agreement_${key}`),
              value: key,
            })),
          ],
        },
      },
      render: ({ record }) => {
        return (
          <div>
            {record?.agreementType
              ? tEnums(`forecast_agreement_${record?.agreementType}`)
              : '-'}
          </div>
        )
      },
      onSubmit: (record, value) => {
        return onForecastSubmit(record, 'agreementType', value)
      },
    },
    {
      title: t('supplierForecastsTableCurrency'),
      key: 'currency',
      value: (record) => record?.currency || 'USD',
      width: '8rem',
      editableProps: {
        type: 'select',
        hasTooltip: false,
        inputProps: {
          options: [
            {
              label: 'MXN',
              value: 'MXN',
            },
            {
              label: 'USD',
              value: 'USD',
            },
          ],
        },
      },
      render: ({ record }) => {
        return <div>{record?.currency ? record?.currency : '-'}</div>
      },
      onSubmit: (record, value) => {
        return onForecastSubmit(record, 'currency', value)
      },
    },
    {
      title: t('supplierForecastsTableCost'),
      key: 'cost',
      value: (record) => record?.cost,
      width: '9rem',
      editableProps: {
        type: 'number',
        hasTooltip: false,
        inputProps: {
          decimalPrecision: DECIMAL_PLACES_DEFAULT,
        },
      },
      render: ({ record }) => {
        return record?.cost
          ? formatCurrency(record?.cost, 'es-MX', record?.currency || 'USD', DECIMAL_PLACES_DEFAULT)
          : '-'
      },
      onSubmit: (record, value) => {
        return onForecastSubmit(record, 'cost', value)
      },
    },
    {
      title: t('supplierForecastsTableCostExpirationDateLabel'),
      key: 'expiresAt',
      value: (record) => record?.expiresAt,
      width: '9rem',
      editableProps: {
        type: 'date-picker',
        hasTooltip: false,
      },
      render: ({ record }) => {
        return record?.expiresAt ? formatDate(record?.expiresAt) : '-'
      },
      onSubmit: (record, value) => {
        return onForecastSubmit(record, 'expiresAt', value)
      },
    },
    {
      title: t('supplierForecastsTableAvailable'),
      key: 'quantity',
      value: (record) => record?.quantity,
      width: '9rem',
      editableProps: {
        type: 'number',
        hasTooltip: false,
        inputProps: {
          decimalPrecision: 0,
        },
      },
      render: ({ record }) => {
        return record?.quantity || record?.quantity === 0
          ? `${formatNumber(record?.quantity, undefined, 0)} Kg`
          : '-'
      },
      onSubmit: (record, value) => {
        return onForecastSubmit(record, 'quantity', value)
      },
    },
  ]

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(Math.ceil(data?.data?.count / +filter.limit))
    }
  }, [data])

  return (
    <div>
      <div className={styles['table-header']}>
        <div className={styles.filters}>
          <InputSearch
            value={filter.search}
            onChange={(value) => handleFilter('search', value)}
            translations={{
              inputPlaceholder: t('supplierForecastsTableSearchPlaceholder'),
            }}
          />

          <Button
            variant="secondary"
            onClick={() =>
              setQueryParams({
                ...queryParams,
                supplierProductFilterForm: 'true',
              })
            }
          >
            <FilterIcon />
            {t('supplierForecastsTableFilters')}
            {getContentTotalFiltered()}
          </Button>
        </div>
      </div>
      <TableEditable
        columns={columns}
        data={data?.data?.results || []}
        isLoading={isLoading}
        sortBy={filter.ordering || ''}
        onSortBy={(value) => {
          onHandleSortBy(value)
        }}
        limit={filter?.limit ? Number(filter.limit) : 20}
      />

      {!isLoading &&
        (!data?.data?.results || data?.data?.results?.length === 0) && (
          <EmptyState
            title={t('supplierForecastsTableEmptyTitle')}
            description={''}
          />
        )}

      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('supplierPaginationShowRegisters', {
              showNumber:
                (data?.data?.count || 0) < +filter.limit
                  ? data?.data?.count
                  : +filter.limit || 20,
              totalNumber: data?.data?.count || 0,
            })}
          </Text>

          <Select
            value={filter?.limit || `20`}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', `${page}`)}
          currentPage={+filter.page as number}
        />
      </div>
      <DrawerSupplierForecastFilter
        filter={filter}
        onHandleFilter={setFilter}
        isOpen={!!queryParams.supplierProductFilterForm}
        onHandleOpen={() => setQueryParams({ supplierProductFilterForm: '' })}
      />
    </div>
  )
}
